import { useEffect, useRef, useState } from "react";
import { ShieldAlert, X } from "lucide-react";
import api from "../../lib/api";

export default function CaptchaSolver() {
  const [cap, setCap] = useState(null);
  const [dragVal, setDragVal] = useState(0);
  const imgRef = useRef(null);
  const throttleRef = useRef(0);

  useEffect(() => {
    let alive = true;
    const poll = () => {
      api.get("/admin/captcha/status").then((r) => {
        if (!alive) return;
        setCap(r.data.active ? r.data : null);
      }).catch(() => {});
    };
    poll();
    const t = setInterval(poll, 600);
    return () => { alive = false; clearInterval(t); };
  }, []);

  if (!cap) return null;

  const toNatural = (val) => {
    if (!imgRef.current) return val;
    const scale = imgRef.current.naturalWidth / (imgRef.current.clientWidth || 1);
    return Math.round(val * scale);
  };

  const onSlide = (val) => {
    setDragVal(val);
    const now = Date.now();
    if (now - throttleRef.current >= 120) {
      throttleRef.current = now;
      api.post("/admin/captcha/drag", { dx: toNatural(val), release: false }).catch(() => {});
    }
  };

  const onRelease = () => {
    api.post("/admin/captcha/drag", { dx: toNatural(dragVal), release: true }).catch(() => {});
    setDragVal(0);
  };

  const cancel = async () => {
    try { await api.post("/admin/captcha/cancel"); } catch {}
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" data-testid="captcha-overlay">
      <div className="w-full max-w-2xl rounded-2xl border border-amber-400/30 bg-[#0A0E17] p-5 shadow-2xl">
        <div className="flex items-center gap-2 mb-3">
          <ShieldAlert size={18} className="text-amber-300" />
          <h3 className="text-sm font-bold text-white mr-auto">TikTok Güvenlik Doğrulaması</h3>
          <span className="text-[10px] font-semibold text-[#64748B]" data-testid="captcha-source">
            {cap.source === "dm" ? "Toplu Mesaj" : "Yayıncı Kontrol"}
          </span>
          <button onClick={cancel} data-testid="captcha-cancel-button" aria-label="Doğrulamayı iptal et"
            className="text-[#64748B] hover:text-red-300 transition-colors"><X size={16} /></button>
        </div>
        <p className="text-[11px] text-[#A0ABC0] mb-3" data-testid="captcha-status-text">
          Kaydırıcıyı sürüklerken hareket <span className="text-cyan-300 font-semibold">canlı olarak</span> TikTok'a uygulanır; ekran her saniye yenilenir. Doğru konuma gelince kaydırıcıyı bırakın. Yanlış olursa tekrar deneyebilirsiniz.
          {cap.expires_in > 0 && <span className="text-amber-300 font-semibold"> Kalan süre: {cap.expires_in} sn</span>}
        </p>
        {cap.screenshot && (
          <img ref={imgRef} src={`data:image/jpeg;base64,${cap.screenshot}`} alt="TikTok doğrulama ekranı"
            data-testid="captcha-screenshot" className="w-full rounded-xl border border-white/10 mb-4 select-none" draggable={false} />
        )}
        <div className="flex items-center gap-3">
          <input type="range" min="0" max="300" value={dragVal} data-testid="captcha-slider"
            onChange={(e) => onSlide(Number(e.target.value))}
            onMouseUp={onRelease}
            onTouchEnd={onRelease}
            className="flex-1 accent-cyan-400 h-2 cursor-grab active:cursor-grabbing" />
          <span className="text-[11px] font-mono text-cyan-300 w-10 text-right" data-testid="captcha-slider-value">{dragVal}px</span>
        </div>
        <p className="text-[10px] text-[#64748B] mt-2">İpucu: Küçük adımlarla sürükleyip ekranın yenilenmesini bekleyin. Doğruysa doğrulama otomatik kapanır ve işlem devam eder.</p>
      </div>
    </div>
  );
}
